import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PageToolbarComponent } from '../../components/page-toolbar/page-toolbar.component';
import { AttendeeService } from '../../services/attendee.service';
import { AttendeeDto } from '../../dto/attendee.dto';
import { QueryResult, QueryResultItem } from '../../components/common-dto/query.dto';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-attendees-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    TableModule,
    ProgressSpinnerModule,
    PageToolbarComponent,
  ],
  template: `
    <pb-page-toolbar
      [header]="'Attendees'"
      [supportsAdd]="false"
      [supportsEdit]="false"
      [isEditing]="false"
    ></pb-page-toolbar>

    <div class="attendees-container">
      <div class="search-container">
        <span class="p-input-icon-right full-width">
          <i class="pi pi-search"></i>
          <input
            type="text"
            pInputText
            [(ngModel)]="searchQuery"
            (ngModelChange)="onSearchInput()"
            placeholder="Search attendees by name, email, company, or title..."
            class="auto-width"
          />
        </span>
      </div>

      <p-table
        [value]="attendees"
        [paginator]="true"
        [rows]="10"
        [rowsPerPageOptions]="[10, 25, 50]"
        [totalRecords]="totalRecords"
        [lazy]="true"
        (onLazyLoad)="loadData($event)"
        [loading]="loading"
      >
        <ng-template pTemplate="header">
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Company</th>
            <th>Title</th>
            <th>Phone</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-attendee>
          <tr>
            <td>{{ attendee.firstName }} {{ attendee.lastName }}</td>
            <td>{{ attendee.email }}</td>
            <td>{{ attendee.company || '-' }}</td>
            <td>{{ attendee.title || '-' }}</td>
            <td>{{ attendee.phone || '-' }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="5" class="text-center">
              No attendees found
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  `,
  styles: [
    `
      .attendees-container {
        padding: 1rem;
      }

      .search-container {
        margin-bottom: 1.5rem;
      }

      .full-width {
        width: 100%;
        display: block;
      }

      .auto-width {
        width: 100%;
      }

      .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem;
        gap: 1rem;
      }

      :host ::ng-deep .p-input-icon-right {
        width: 100%;
      }

      :host ::ng-deep .p-input-icon-right input {
        width: 100%;
      }
    `,
  ],
})
export class AttendeesListComponent implements OnInit {
  attendees: AttendeeDto[] = [];
  searchQuery: string = '';
  loading = true;
  totalRecords = 0;
  conferenceId: string = '';

  private searchSubject = new Subject<string>();

  constructor(
    private attendeeService: AttendeeService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.conferenceId = this.route.snapshot.params['conferenceId'] || '';

    // Setup debounced search
    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.loadData({ first: 0, rows: 10 });
      });

    this.loadData({ first: 0, rows: 10 });
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchQuery);
  }

  loadData(event: any): void {
    this.loading = true;
    const skip = event.first || 0;
    const take = event.rows || 10;

    const queryParams: any = {
      skip,
      take,
    };

    if (this.searchQuery && this.searchQuery.trim() !== '') {
      queryParams.filter = this.searchQuery.trim();
    }

    this.attendeeService
      .getAttendees(this.conferenceId, queryParams)
      .subscribe({
        next: (result: QueryResult<AttendeeDto>) => {
          this.attendees = result.items.map((item) => item.item);
          this.totalRecords = result.total;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading attendees:', error);
          this.loading = false;
        },
      });
  }
}

